/**
 * OAuth consent: /connect?request=<id>. The authorization server validated
 * the client's request and parked it; here the signed-in user sees who is
 * asking and approves or denies. Signed-out visitors go through the ordinary
 * gate first, with the request id stashed so they land back here.
 */
import { useEffect, useState } from "react";
import { Check, FileText, ShieldCheck, SquareCheckBig, StickyNote } from "lucide-react";
import type { OAuthRequestDto } from "@hydra-notes/shared";
import { useAuth } from "@/data/auth";
import { AuthGate, Splash } from "@/components/AuthGate";
import { Button } from "@/ui/Button";
import { LogoMark, Wordmark } from "@/ui/Logo";
import s from "./developers.module.css";

export const PENDING_CONNECT_KEY = "hn.pendingConnect";

export function ConnectPage() {
  const status = useAuth((a) => a.status);
  const user = useAuth((a) => a.user);
  const requestId = new URLSearchParams(location.search).get("request") ?? "";
  const [req, setReq] = useState<OAuthRequestDto | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status === "signedOut" && requestId) localStorage.setItem(PENDING_CONNECT_KEY, requestId);
    if (status === "signedIn") localStorage.removeItem(PENDING_CONNECT_KEY);
  }, [status, requestId]);

  useEffect(() => {
    if (status !== "signedIn" || !requestId) return;
    fetch(`/api/oauth/requests/${requestId}`, { credentials: "same-origin" })
      .then(async (r) => {
        const body = (await r.json()) as { request?: OAuthRequestDto; error?: string };
        if (!r.ok || !body.request) throw new Error(body.error ?? "request not found");
        setReq(body.request);
      })
      .catch((e: Error) => setError(e.message));
  }, [status, requestId]);

  const decide = async (verb: "approve" | "deny") => {
    if (!req || busy) return;
    setBusy(true);
    const r = await fetch(`/api/oauth/requests/${req.id}/${verb}`, { method: "POST", credentials: "same-origin" });
    const body = (await r.json().catch(() => ({}))) as { redirect?: string; error?: string };
    if (!r.ok || !body.redirect) {
      setBusy(false);
      setError(body.error ?? "something went wrong");
      return;
    }
    location.href = body.redirect;
  };

  if (status === "loading") return <Splash />;
  if (status !== "signedIn") return <AuthGate />;

  return (
    <div className={s.consent} data-connect-page>
      <div className={s.consentCard}>
        <div className={s.consentBrand}>
          <LogoMark size={26} />
          <Wordmark size={15} />
        </div>
        {!requestId ? (
          <p className={s.consentText}>This page needs a connection request. Start from your assistant and it will bring you here.</p>
        ) : error ? (
          <>
            <h1 className={s.consentTitle}>That request is no longer valid</h1>
            <p className={s.consentText}>{error}. Start the connection again from your assistant.</p>
            <div className={s.consentActions}>
              <Button variant="subtle" onClick={() => (location.href = "/app")}>
                Back to the app
              </Button>
            </div>
          </>
        ) : !req ? (
          <p className={s.consentText}>Checking the request...</p>
        ) : (
          <>
            <h1 className={s.consentTitle}>
              Let <strong>{req.clientName}</strong> use your Hydra Notes?
            </h1>
            <p className={s.consentText}>
              It will act as <strong>{user?.displayName || user?.email}</strong> with {req.scope === "write" ? "read and write" : "read-only"} access. While it works you will see its name in the app: a caret in your notes, a chip on your task lists and card boards.
            </p>
            <ul className={s.consentList}>
              <li>
                <FileText size={15} /> Notes: search, read{req.scope === "write" ? ", create and edit" : ""}
              </li>
              <li>
                <SquareCheckBig size={15} /> Tasks: list{req.scope === "write" ? ", add, complete and reorder" : ""}
              </li>
              <li>
                <StickyNote size={15} /> Cards: list{req.scope === "write" ? ", create and arrange" : ""}
              </li>
              <li>
                <ShieldCheck size={15} /> Never permanently deletes anything. Revoke any time in Settings, Developers.
              </li>
            </ul>
            <div className={s.consentActions}>
              <Button variant="ghost" disabled={busy} onClick={() => void decide("deny")}>
                Deny
              </Button>
              <Button variant="solid" disabled={busy} onClick={() => void decide("approve")}>
                <Check size={14} /> Approve
              </Button>
            </div>
            <span className={s.consentMeta}>Returns to {req.redirectHost}. This request expires in a few minutes.</span>
          </>
        )}
      </div>
    </div>
  );
}
